import { useState, useEffect } from 'react'
import { supabase } from './supabase'
import { checkPlatformAdmin } from './tenant'

/**
 * ¿El usuario autenticado es super administrador de plataforma?
 *
 * Reutiliza EXACTAMENTE el mecanismo de autorización que ya existe en
 * NOIZA (`checkPlatformAdmin`, que consulta la tabla `platform_admins`
 * y exige `is_active` con rol `super_admin` o `platform_admin`). No se
 * introduce ningún sistema de roles nuevo, ni listas de correos, ids o
 * nombres.
 *
 * Ante cualquier error devuelve `false`: la excepción de soporte solo se
 * concede cuando el rol de plataforma se confirma positivamente.
 */
export function useIsPlatformAdmin(userId: string | undefined) {
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!userId) {
      setIsPlatformAdmin(false)
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)

    checkPlatformAdmin(userId)
      .then((result) => {
        if (cancelled) return
        setIsPlatformAdmin(result === true)
      })
      .catch(() => {
        if (cancelled) return
        setIsPlatformAdmin(false)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [userId])

  return { isPlatformAdmin, loading }
}

/**
 * Estado de acceso administrativo de un restaurante.
 *
 * Vive en `public.restaurants.access_status`. El único valor que habilita
 * la operación es `'active'`; cualquier otro valor bloquea las áreas
 * operativas del panel para ese restaurante.
 *
 * El valor NO se interpreta contra una lista cerrada a propósito: si mañana
 * se agrega otro motivo de suspensión desde la base, el bloqueo funciona
 * igual sin tocar el frontend.
 */
export interface RestaurantAccessStatus {
  id: string
  access_status: string | null
  suspension_reason: string | null
  suspension_message: string | null
  suspended_at: string | null
}

/** Único valor que permite operar. Todo lo demás suspende. */
export const ACCESS_STATUS_ACTIVE = 'active'

export function isRestaurantActive(
  status: RestaurantAccessStatus | null,
): boolean {
  if (!status) return true
  // `null` / vacío se trata como activo: restaurantes creados antes de que
  // existiera la columna no deben quedar bloqueados por omisión.
  const value = (status.access_status || ACCESS_STATUS_ACTIVE).trim()
  return value.toLowerCase() === ACCESS_STATUS_ACTIVE
}

/**
 * Lee el estado de acceso del restaurante actual.
 *
 * SOLO LECTURA. Nunca escribe en Supabase, no toca sesiones, pedidos, pagos
 * ni configuración. Solo trae las cinco columnas necesarias.
 *
 * Ante un error de red o de permisos NO bloquea: se registra el problema y
 * se deja pasar (fail-open). Un fallo transitorio de conexión no puede
 * dejar sin sistema a un restaurante al día.
 *
 * @param restaurantId  Id real del restaurante resuelto del contexto actual.
 * @param recheckKey    Valor que, al cambiar, fuerza una nueva verificación
 *                      (se usa la ruta activa para revalidar al navegar).
 */
export function useRestaurantAccessStatus(
  restaurantId: string | undefined,
  recheckKey?: string,
) {
  const [status, setStatus] = useState<RestaurantAccessStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [failedOpen, setFailedOpen] = useState(false)

  useEffect(() => {
    if (!restaurantId) {
      setStatus(null)
      setLoading(false)
      return
    }

    let cancelled = false

    async function fetchStatus() {
      try {
        setLoading(true)
        const { data, error } = await supabase
          .from('restaurants')
          .select(
            'id, access_status, suspension_reason, suspension_message, suspended_at',
          )
          .eq('id', restaurantId)
          .maybeSingle()

        if (cancelled) return

        if (error) {
          console.warn(
            '[NOIZA][access-gate] no se pudo leer access_status (fail-open)',
            {
              restaurant_id: restaurantId,
              message: error.message,
              code: (error as any).code,
            },
          )
          setStatus(null)
          setFailedOpen(true)
          return
        }

        setStatus((data as RestaurantAccessStatus) || null)
        setFailedOpen(false)
      } catch (err) {
        if (cancelled) return
        console.warn(
          '[NOIZA][access-gate] excepción leyendo access_status (fail-open)',
          err,
        )
        setStatus(null)
        setFailedOpen(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchStatus()

    return () => {
      cancelled = true
    }
  }, [restaurantId, recheckKey])

  return { status, loading, failedOpen }
}

/**
 * Lee el estado de acceso de un restaurante A PARTIR DE SU SLUG, para la
 * cara pública (visitante anónimo).
 *
 * Usa la función `public_restaurant_access_status`, que devuelve solo `id`
 * y `access_status` — nunca el motivo ni el mensaje interno de suspensión.
 * Es la misma técnica que ya usa el sitio para resolver restaurantes que no
 * están publicados en el marketplace.
 *
 * SOLO LECTURA y fail-open: ante un error de red o si la función todavía no
 * está instalada, se deja pasar y se registra el problema. Un corte
 * transitorio no puede tumbar la página de un restaurante al día.
 */
export function usePublicRestaurantAccess(slug: string | undefined) {
  const [status, setStatus] = useState<RestaurantAccessStatus | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!slug) {
      setStatus(null)
      setLoading(false)
      return
    }

    let cancelled = false

    async function fetchStatus() {
      try {
        setLoading(true)
        const { data, error } = await supabase.rpc(
          'public_restaurant_access_status',
          { p_slug: slug },
        )

        if (cancelled) return

        if (error) {
          console.warn(
            '[NOIZA][access-gate] no se pudo leer el estado público (fail-open)',
            { slug, message: error.message, code: (error as any).code },
          )
          setStatus(null)
          return
        }

        const row = (Array.isArray(data) ? (data[0] ?? null) : data) as {
          id: string
          access_status: string | null
        } | null

        setStatus(
          row
            ? {
                id: String(row.id),
                access_status: row.access_status ?? null,
                suspension_reason: null,
                suspension_message: null,
                suspended_at: null,
              }
            : null,
        )
      } catch (err) {
        if (cancelled) return
        console.warn(
          '[NOIZA][access-gate] excepción leyendo el estado público (fail-open)',
          err,
        )
        setStatus(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchStatus()

    return () => {
      cancelled = true
    }
  }, [slug])

  return { status, loading }
}
